import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Stack,
  Tooltip,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  ContentCut as ContentCutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  LocalHospital as HealthIcon,
  FitnessCenter as FitnessIcon,
  Spa as SpaIcon,
  Face as BeautyIcon,
  School as EducationIcon,
  Build as MaintenanceIcon,
  Computer as TechIcon,
  Restaurant as FoodIcon,
  DirectionsCar as TransportIcon,
  Home as HomeIcon,
  Pets as PetsIcon,
  ChildCare as ChildCareIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import { usePlacesData } from '../../contexts/PlacesDataContext';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import PaymentIcon from '../ui/PaymentIcon';
import { getInitials } from '../../utils/professionalNameUtils';
import ProfessionalAvatar from '../ProfessionalAvatar';

// Helper para obter ícone dinamicamente
const getIconComponent = (iconName) => {
  const IconComponent = MuiIcons[iconName];
  return IconComponent || MuiIcons.HelpOutline;
};

/**
 * Retorna ícone apropriado baseado na categoria do serviço
 */
const getCategoryIcon = (category) => {
  if (!category) return ContentCutIcon;
  
  const categoryLower = category.toLowerCase();
  
  // Saúde e Medicina
  if (categoryLower.includes('saúde') || categoryLower.includes('saude') || 
      categoryLower.includes('médico') || categoryLower.includes('medico') ||
      categoryLower.includes('clínica') || categoryLower.includes('clinica') ||
      categoryLower.includes('hospital') || categoryLower.includes('odonto')) {
    return HealthIcon;
  }
  
  // Beleza e Estética
  if (categoryLower.includes('beleza') || categoryLower.includes('estética') || 
      categoryLower.includes('estetica') || categoryLower.includes('cabelo') ||
      categoryLower.includes('manicure') || categoryLower.includes('pedicure') ||
      categoryLower.includes('maquiagem') || categoryLower.includes('salão') ||
      categoryLower.includes('salao') || categoryLower.includes('barbeiro')) {
    return BeautyIcon;
  }
  
  // Bem-estar e Spa
  if (categoryLower.includes('spa') || categoryLower.includes('massagem') ||
      categoryLower.includes('relaxamento') || categoryLower.includes('terapia')) {
    return SpaIcon;
  }
  
  // Fitness e Esportes
  if (categoryLower.includes('fitness') || categoryLower.includes('academia') ||
      categoryLower.includes('personal') || categoryLower.includes('esporte') ||
      categoryLower.includes('yoga') || categoryLower.includes('pilates')) {
    return FitnessIcon;
  }
  
  // Educação e Cursos
  if (categoryLower.includes('educação') || categoryLower.includes('educacao') ||
      categoryLower.includes('curso') || categoryLower.includes('aula') ||
      categoryLower.includes('ensino') || categoryLower.includes('escola')) {
    return EducationIcon;
  }
  
  // Tecnologia e Informática
  if (categoryLower.includes('tecnologia') || categoryLower.includes('informática') ||
      categoryLower.includes('informatica') || categoryLower.includes('computador') ||
      categoryLower.includes('software') || categoryLower.includes('ti')) {
    return TechIcon;
  }
  
  // Manutenção e Reparos
  if (categoryLower.includes('manutenção') || categoryLower.includes('manutencao') ||
      categoryLower.includes('reparo') || categoryLower.includes('conserto') ||
      categoryLower.includes('instalação') || categoryLower.includes('instalacao')) {
    return MaintenanceIcon;
  }
  
  // Alimentação e Gastronomia
  if (categoryLower.includes('alimentação') || categoryLower.includes('alimentacao') ||
      categoryLower.includes('restaurante') || categoryLower.includes('comida') ||
      categoryLower.includes('gastronomia') || categoryLower.includes('chef')) {
    return FoodIcon;
  }
  
  // Transporte
  if (categoryLower.includes('transporte') || categoryLower.includes('motorista') ||
      categoryLower.includes('táxi') || categoryLower.includes('taxi') ||
      categoryLower.includes('uber') || categoryLower.includes('delivery')) {
    return TransportIcon;
  }
  
  // Serviços Domésticos
  if (categoryLower.includes('doméstico') || categoryLower.includes('domestico') ||
      categoryLower.includes('limpeza') || categoryLower.includes('casa') ||
      categoryLower.includes('lar')) {
    return HomeIcon;
  }
  
  // Pet Services
  if (categoryLower.includes('pet') || categoryLower.includes('animal') ||
      categoryLower.includes('veterinário') || categoryLower.includes('veterinario') ||
      categoryLower.includes('cachorro') || categoryLower.includes('gato')) {
    return PetsIcon;
  }
  
  // Cuidados Infantis
  if (categoryLower.includes('infantil') || categoryLower.includes('criança') ||
      categoryLower.includes('crianca') || categoryLower.includes('bebê') ||
      categoryLower.includes('bebe') || categoryLower.includes('babá')) {
    return ChildCareIcon;
  }
  
  // Default
  return ContentCutIcon;
};

/**
 * PlaceCardNormalized
 * 
 * Card que usa estrutura normalizada com lookup de dados via context
 * Não faz mais chamadas API individuais - usa cache local
 */
export default function PlaceCardNormalized({ 
  place, 
  onEdit, 
  onDelete,
  onConfigurePayments,
  onManageServices,
  onManageProfessionals,
  onRemoveService,
  onRemoveProfessional,
  onConfigureProfessionalServices
}) {
  const { getService, getPayment, getProfessional } = usePlacesData();
  const { getIcon } = useThemeCustom();

  return (
    <Card elevation={2}>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {React.createElement(getIconComponent(getIcon('places').name), { color: 'primary' })}
            <Typography variant="h6" fontWeight="bold">
              {place.name}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Editar local">
              <IconButton size="small" onClick={() => onEdit(place)} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir local">
              <IconButton size="small" onClick={() => onDelete(place)} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Informações do local */}
        {place.description && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {place.description}
          </Typography>
        )}

        <Stack spacing={0.5} mb={2}>
          {place.address && (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {place.address}
              </Typography>
            </Box>
          )}
          {place.phone && (
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {place.phone}
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Profissionais */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                Profissionais ({place.professionals?.length || 0})
              </Typography>
            </Box>
            <Tooltip title="Gerenciar profissionais">
              <IconButton size="small" onClick={() => onManageProfessionals(place)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          {place.professionals && place.professionals.length > 0 ? (
            <List dense disablePadding>
              {place.professionals.map((link) => {
                const professional = getProfessional(link.professionalId);
                if (!professional) return null;
                
                const initials = getInitials(professional.name || 'Profissional');
                
                // Criar objeto expandido com dados do profissional para facilitar remoção
                const expandedLink = {
                  ...link,
                  professional: professional,
                  professionalName: professional?.name
                };
                
                // Buscar serviços vinculados ao profissional
                const professionalServiceIds = link.serviceIds || [];
                const professionalServices = professionalServiceIds
                  .map(serviceId => getService(serviceId))
                  .filter(Boolean);
                
                return (
                  <ListItem 
                    key={link.id} 
                    dense 
                    disableGutters
                    sx={{ position: 'relative', alignItems: 'flex-start', py: 1 }}
                  >
                    {/* Avatar com foto ou iniciais */}
                    <ListItemAvatar>
                      <ProfessionalAvatar
                        professionalId={professional.id}
                        photoTimestamp={professional.photoTimestamp}
                        name={professional.name}
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem'
                        }}
                      >
                        {initials}
                      </ProfessionalAvatar>
                    </ListItemAvatar>
                    
                    {/* Nome, especialidade e serviços */}
                    <ListItemText
                      disableTypography
                      sx={{ pr: 5 }} // Padding para não sobrepor o botão delete
                      primary={
                        <Box>
                          {/* Linha 1: Nome do profissional */}
                          <Typography variant="body2" fontWeight="medium">
                            {professional.name}
                          </Typography>
                          
                          {/* Linha 2: Especialidade */}
                          {professional.specialty && (
                            <Typography variant="caption" color="text.secondary">
                              {professional.specialty}
                            </Typography>
                          )}
                          
                          {/* Linha 3: Serviços com botão configurar */}
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                            {professionalServices.length > 0 ? (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  {professionalServices.map(s => s.name).join(', ')}
                                </Typography>
                                <Tooltip title="Configurar serviços do profissional">
                                  <IconButton
                                    size="small"
                                    onClick={() => onConfigureProfessionalServices(place, expandedLink)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <SettingsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                  Nenhum serviço
                                </Typography>
                                <Tooltip title="Adicionar serviços">
                                  <IconButton
                                    size="small"
                                    onClick={() => onConfigureProfessionalServices(place, expandedLink)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    
                    {/* Botão de remover */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 0,
                      }}
                    >
                      <Tooltip title="Remover profissional do local">
                        <IconButton 
                          size="small" 
                          onClick={() => onRemoveProfessional(place, expandedLink)}
                          color="error"
                        >
                          <RemoveCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Nenhum profissional vinculado
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Serviços */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <ContentCutIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                Serviços ({place.services?.length || 0})
              </Typography>
            </Box>
            <Tooltip title="Gerenciar serviços">
              <IconButton size="small" onClick={() => onManageServices(place)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          {place.services && place.services.length > 0 ? (
            <List dense disablePadding>
              {place.services.map((serviceLink) => {
                const service = getService(serviceLink.serviceId);
                if (!service) return null;
                
                const CategoryIcon = getCategoryIcon(service.category);
                
                // Criar objeto expandido com dados do serviço para facilitar remoção
                const expandedServiceLink = {
                  ...serviceLink,
                  service: service,
                  serviceName: service?.name
                };
                
                return (
                  <ListItem 
                    key={serviceLink.id} 
                    dense 
                    disableGutters
                    sx={{ 
                      alignItems: 'flex-start',
                      py: 1,
                      position: 'relative'
                    }}
                  >
                    {/* Botão de configuração do serviço (editar/deletar) - canto superior direito */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 0,
                      }}
                    >
                      <Tooltip title="Remover serviço do local">
                        <IconButton 
                          size="small" 
                          onClick={() => onRemoveService(place, expandedServiceLink)}
                          color="error"
                        >
                          <RemoveCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      <CategoryIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      sx={{ pr: 5 }} // Padding para não sobrepor o botão delete
                      primary={
                        <Box>
                          {/* Linha 1: Nome do serviço */}
                          <Typography variant="body2" fontWeight="medium">
                            {service.name}
                          </Typography>
                          
                          {/* Linha 2: Formas de pagamento aceitas + botão configurar pagamentos */}
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                            {serviceLink.payments && serviceLink.payments.length > 0 ? (
                              <>
                                {/* Ícones de pagamento */}
                                {serviceLink.payments
                                  .map(paymentLink => {
                                    const payment = getPayment(paymentLink.paymentId);
                                    return payment ? { ...payment, linkId: paymentLink.id } : null;
                                  })
                                  .filter(Boolean)
                                  .sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999))
                                  .slice(0, 5)
                                  .map((payment) => (
                                    <Tooltip key={payment.linkId} title={payment.name}>
                                      <Box>
                                        <PaymentIcon
                                          displayOrder={payment.displayOrder}
                                          size={24}
                                        />
                                      </Box>
                                    </Tooltip>
                                  ))}

                                {/* Badge de +N se houver mais de 5 */}
                                {serviceLink.payments.length > 5 && (
                                  <Chip
                                    label={`+${serviceLink.payments.length - 5}`}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}

                                {/* Botão de configurar pagamentos - em sequência */}
                                <Tooltip title="Configurar formas de pagamento">
                                  <IconButton
                                    size="small"
                                    onClick={() => onConfigurePayments(serviceLink)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <SettingsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                  Nenhuma forma de pagamento
                                </Typography>
                                <Tooltip title="Configurar formas de pagamento">
                                  <IconButton
                                    size="small"
                                    onClick={() => onConfigurePayments(serviceLink)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <SettingsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Nenhum serviço vinculado
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

PlaceCardNormalized.propTypes = {
  place: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConfigurePayments: PropTypes.func,
  onManageServices: PropTypes.func,
  onManageProfessionals: PropTypes.func,
  onRemoveService: PropTypes.func,
  onRemoveProfessional: PropTypes.func,
  onConfigureProfessionalServices: PropTypes.func,
};
